/**
 * Admin V3 — Operations / Audit — top-level page
 *
 * Mounted at: /admin-v3/operations/audit (see AdminV3.js)
 *
 * GOVERNANCE MEANING: Operator action audit log (read-only).
 * Mirror of legacy AuditLogPanel (AdminControlTower.js:968-1068). The
 * operator_actions collection is mandatory compliance evidence; audit is
 * structurally read-only — no Phase 3+ mutation can change this.
 *
 * SCOPE FOR PHASE 2.6:
 *   - GET /admin/control-tower/operator-log (paginated, single-axis filter)
 *   - GET /admin/control-tower/operator-log/stats (summary line)
 *   - Server-side filter chips: action_type dropdown + operator_id input
 *     (backend if/elif precedence: operator_id wins when both are set —
 *     admin_control_tower_v2.py:1359-1368; UI shows a hint when both set)
 *   - No polling (manual refresh via retry button on error only)
 *   - Per-row expansion with before/after JSON + IP/UA/session/intervention
 *   - 100-cap hint when result length === limit
 *
 * EXPLICIT ZERO MUTATIONS:
 *   - No POST/PATCH/PUT/DELETE; audit is read-only by construction
 */

import React, { useState, useEffect } from 'react';
import { History } from 'lucide-react';
import { SectionCard } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';
import OperationsAuditSearchBar from './OperationsAuditSearchBar';
import OperationsAuditTable from './OperationsAuditTable';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_LIMIT = 100;

const OperationsAuditPage = () => {
  const locale = DEFAULT_LOCALE;

  const [actionType, setActionType] = useState('');  // '' = all
  const [operatorId, setOperatorId] = useState('');
  const [debouncedOperatorId, setDebouncedOperatorId] = useState('');

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedOperatorId(operatorId.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [operatorId]);

  // Build query params. Backend has single-axis precedence (operator_id wins).
  // We send what the user typed; the UI hint already explains the limitation.
  const queryParams = { limit: DEFAULT_LIMIT };
  if (debouncedOperatorId) queryParams.operator_id = debouncedOperatorId;
  if (actionType)          queryParams.action_type = actionType;

  const {
    data: logData,
    loading: logLoading,
    error: logError,
    refetch: logRefetch,
  } = useAdminQuery('/admin/control-tower/operator-log', queryParams);

  const {
    data: statsData,
    loading: statsLoading,
  } = useAdminQuery('/admin/control-tower/operator-log/stats');

  const actions = Array.isArray(logData?.actions) ? logData.actions : [];
  const totalActions = statsData?.total_actions ?? 0;
  const uniqueOperators = Array.isArray(statsData?.unique_operators)
    ? statsData.unique_operators.length
    : 0;

  const isFilterActive = Boolean(debouncedOperatorId) || Boolean(actionType);
  const isAtBackendCap = actions.length === DEFAULT_LIMIT;
  const showSkeleton = logLoading && !logData;

  return (
    <SectionCard
      title={t('operations.audit.title', locale)}
      icon={History}
      iconClass="bg-indigo-100 text-indigo-600"
    >
      <div className="space-y-4">
        <OperationsAuditSearchBar
          actionType={actionType}
          onActionTypeChange={setActionType}
          operatorId={operatorId}
          onOperatorIdChange={setOperatorId}
          locale={locale}
        />

        {/* Summary line */}
        {!statsLoading && statsData && (
          <div
            className="text-[12px] text-slate-500 flex flex-wrap gap-x-3 gap-y-1"
            data-testid="adminv3-operations-audit-summary"
          >
            <span>{t('operations.audit.summary.totalActions', locale).replace('{n}', String(totalActions))}</span>
            <span className="text-slate-300">·</span>
            <span>{t('operations.audit.summary.uniqueOps', locale).replace('{n}', String(uniqueOperators))}</span>
          </div>
        )}

        <OperationsAuditTable
          actions={actions}
          loading={showSkeleton}
          error={logError}
          isFilterActive={isFilterActive}
          onRetry={logRefetch}
          locale={locale}
        />

        {/* 100-cap governance hint */}
        {isAtBackendCap && !logLoading && (
          <p
            className="text-[12px] text-slate-400 italic"
            data-testid="adminv3-operations-audit-cap-hint"
          >
            {t('operations.audit.cap.hint', locale).replace('{n}', String(DEFAULT_LIMIT))}
          </p>
        )}
      </div>
    </SectionCard>
  );
};

export default OperationsAuditPage;
